import { ILoyaltyRuleConditionEvaluation } from 'generated/graphql-gateway';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import {
  AppOffer,
  AppPersonalizedOffer,
  EngineSystemwideOffer,
  IncentiveSource,
  LoyaltyEngineId,
  SanitySystemwideOffer,
  appOfferToLoyaltyOffer,
  buildAppSystemwideOffer,
  isAppPersonalizedOffer,
  isAppSystemwideOffer,
  isSanitySystemwideOffer,
} from 'state/loyalty/types';
import { incentiveIsOfferBenefit } from 'state/loyalty/utils';

import { ServiceModeUnavailableMessageGenerator } from '../loyalty-service-modes';
import { IOffersFilters } from '../types';

type OfferEvaluatorResponse = {
  destination: 'CART' | 'TOP' | 'CENTER' | 'BOTTOM';
  appOffer?: AppOffer;
  offerFeedback: Map<LoyaltyEngineId, ILoyaltyRuleConditionEvaluation[]>;
  skipReasons: { offer: Offer; reason: string }[];
};

type OfferEvaluatorHelpers = {
  isAppliedOffer: (offer: AppOffer) => boolean;
  isInRestaurantRedemption: (offer: AppOffer) => boolean;
  getEngineSystemwideOfferByLoyaltyEngineId: (id: LoyaltyEngineId) => EngineSystemwideOffer;
  // Local function type to avoid importing from hooks and creating circular deps
  checkLoyaltyOfferAvailability: (offer: any) => boolean;
  getIncentiveUnavailableMessage: (incentive: AppOffer) => string | undefined;
  appliedFilters: IOffersFilters;
  generateServiceModeUnavailableMessage: ServiceModeUnavailableMessageGenerator;
  enableSortOffersForServiceMode: boolean;
};

type OfferEvaluatorContext = {
  helpers: OfferEvaluatorHelpers;
  res: OfferEvaluatorResponse;
};

type Offer = AppPersonalizedOffer | SanitySystemwideOffer;
type Step = (offer: Offer, context: OfferEvaluatorContext) => string | void;

export function buildOfferEvaluator(helpers: OfferEvaluatorHelpers) {
  const steps: Step[] = [
    guardOfferType,
    guardUnavailable,
    guardNotOfferBenefit,
    buildAppOffer,
    guardByAppliedServiceModeFilter,
    guardByInStoreFilter,
    applyUnavailableMessageInlineAlert,
    applyServiceModeInlineAlert,
    routeOffer,
  ];
  return function evaluateOffer(offer: Offer) {
    const res: OfferEvaluatorResponse = {
      destination: 'CENTER',
      offerFeedback: new Map(),
      skipReasons: [],
    };
    const context: OfferEvaluatorContext = { helpers, res };
    for (const step of steps) {
      const error = step(offer, context);
      if (error) {
        res.skipReasons.push({ offer, reason: error });
        // Stop processing chain on false or error
        break;
      }
    }
    return res;
  };
}

const guardOfferType: Step = offer => {
  if (isAppPersonalizedOffer(offer)) {
    if (!offer.loyaltyEngineId || !offer.cmsId) {
      return 'invalid_app_personalized_offer';
    }
    return;
  } else if (isSanitySystemwideOffer(offer)) {
    if (!offer.loyaltyEngineId || !offer._id) {
      return 'invalid_sanity_systemwide_offer';
    }
    return;
  }

  return 'unknown_offer_type';
};

const guardUnavailable: Step = (offer, { helpers }) => {
  if (offer.__typename === 'ConfigOffer' && offer.metadata) {
    const sourceMetadata = offer.metadata.find(metadata => metadata.key === 'source');

    // Show promotion code offers regardless of availability
    if (sourceMetadata?.value === IncentiveSource.PROMOTION_CODE) {
      return;
    }
  }

  if (
    !helpers.checkLoyaltyOfferAvailability(
      isAppPersonalizedOffer(offer) ? appOfferToLoyaltyOffer(offer) : offer
    )
  ) {
    return 'unavailable_offer';
  }
};

const guardNotOfferBenefit: Step = offer => {
  if (!incentiveIsOfferBenefit(offer)) {
    return 'not_offer_benefit';
  }
};

const buildAppOffer: Step = (offer, context) => {
  if (isAppPersonalizedOffer(offer)) {
    // Filter out backend INVALID_SERVICE_MODE errors which may be stale.
    // Service mode validity is re-evaluated client-side to support dynamic toggling.
    const filteredErrors = offer.errors?.filter(
      (error: any) => error.code !== IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
    );
    context.res.appOffer = { ...offer, errors: filteredErrors };
    return;
  }
  const engineOffer = context.helpers.getEngineSystemwideOfferByLoyaltyEngineId(
    offer.loyaltyEngineId
  );
  if (!engineOffer) {
    return 'missing_engine_offer';
  }

  const appSystemwideOffer = buildAppSystemwideOffer(offer, engineOffer);

  // Filter out backend INVALID_SERVICE_MODE errors which may be stale.
  // Service mode validity is re-evaluated client-side to support dynamic toggling.
  if (appSystemwideOffer.errors) {
    appSystemwideOffer.errors = appSystemwideOffer.errors.filter(
      (error: any) => error.code !== IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
    );
  }

  context.res.appOffer = appSystemwideOffer;
};

const guardByAppliedServiceModeFilter: Step = (offer, context) => {
  const { pickUpOnly, deliveryOnly } = context.helpers.appliedFilters;
  if (
    (pickUpOnly || deliveryOnly) &&
    !context.helpers.appliedFilters[context.res.appOffer?.offerServiceMode || '']
  ) {
    return 'service_mode_filter';
  }
};

const guardByInStoreFilter: Step = (offer, context) => {
  if (context.helpers.appliedFilters?.inStoreEnabled && context.res.appOffer?.mobileOrderOnly) {
    return 'in_store_filter';
  }
};

const applyUnavailableMessageInlineAlert: Step = (offer, context) => {
  // By this point, appOffer should be built, but we need to construct
  // a minimal object that matches what getIncentiveUnavailableMessage expects
  const appOffer = context.res.appOffer;
  if (!appOffer) {
    return 'no_app_offer_built';
  }

  const msg = context.helpers.getIncentiveUnavailableMessage(appOffer);
  if (msg) {
    context.res.appOffer = {
      ...appOffer,
      inlineAlertMessage: msg,
    };
  }
};

/**
 * Re-evaluates service mode compatibility client-side against the currently selected mode.
 * This ensures that if the user toggles between Service Modes (e.g. Pickup/Delivery),
 * they get instant feedback without needing a network refresh. This effectively replaces
 * the static (and potentially stale) INVALID_SERVICE_MODE error we filtered out from the backend.
 */
const applyServiceModeInlineAlert: Step = (offer, context) => {
  const msg = context.helpers.generateServiceModeUnavailableMessage(
    context.res.appOffer?.offerServiceMode
  );

  if (msg) {
    context.res.appOffer.inlineAlertMessage = msg;
  }
};

const routeOffer: Step = (offer, context) => {
  const { res } = context;
  // Applied or in-restaurant redemption path
  if (
    context.helpers.isAppliedOffer(res.appOffer) ||
    context.helpers.isInRestaurantRedemption(res.appOffer)
  ) {
    res.destination = 'CART';
    return;
  }

  // Personalized offer path
  if (isAppPersonalizedOffer(res.appOffer)) {
    if (res.appOffer?.errors?.length && res.appOffer.loyaltyEngineId) {
      res.offerFeedback.set(res.appOffer.loyaltyEngineId, [...res.appOffer.errors]);
    }
    res.destination = 'TOP';
    return;
  }

  // Systemwide offer path
  if (isAppSystemwideOffer(res.appOffer)) {
    const offerHasInvalidServiceMode = res.appOffer?.errors?.some(
      ({ code }: any) => code === IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
    );
    if (offerHasInvalidServiceMode && context.helpers.enableSortOffersForServiceMode) {
      res.destination = 'BOTTOM';
    } else {
      res.destination = 'CENTER';
    }

    if (res.appOffer?.errors?.length) {
      res.offerFeedback.set(res.appOffer.loyaltyEngineId, [...res.appOffer.errors]);
    }
    return;
  }

  // If reaches here without engine/personalized (shouldn't happen normally), skip with reason
  return 'no_engine_or_personalized_match';
};
